import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminTableHeader,
  AdminTableBody,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminSupportingText,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  buildSamplePricingPlanManagementRecords,
  formatAuditDisplay,
  formatEffectiveFromDisplay,
  formatMonthlyCostDisplay,
  hasPendingPriceChange,
  resolveCurrentMonthlyCost,
} from '../../../data/pricingPlanManagementData';
import { slugifyName } from '../../../utils/slugify';

const tableCols =
  'minmax(80px, 0.55fr) minmax(64px, 0.45fr) minmax(100px, 0.7fr) minmax(100px, 0.7fr) minmax(96px, 0.65fr) minmax(120px, 0.85fr) minmax(120px, 0.85fr) minmax(96px, 0.65fr) minmax(62px, 0.04fr)';

const TABLE_MIN_WIDTH = '1040px';

const PricingPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState(() => buildSamplePricingPlanManagementRecords());
  const [saveSuccess, setSaveSuccess] = useState('');

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  useEffect(() => {
    const incoming = location.state?.pricingPlans;
    if (Array.isArray(incoming)) {
      setPlans(incoming);
    }
    if (location.state?.saveSuccess) {
      setSaveSuccess(location.state.saveSuccess);
    }
    if (incoming || location.state?.saveSuccess) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const openAddPlan = () => {
    navigate('/admin/pricing-plans/add', { state: { pricingPlans: plans } });
  };

  const openPlanView = (plan) => {
    const planName = slugifyName(plan.planName);
    navigate(`/admin/pricing-plans/${encodeURIComponent(planName)}`, {
      state: { plan, pricingPlans: plans },
    });
  };

  const openPlanEdit = (plan) => {
    const planName = slugifyName(plan.planName);
    navigate(`/admin/pricing-plans/${encodeURIComponent(planName)}/edit`, {
      state: { plan, pricingPlans: plans },
    });
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Pricing Plans</AdminPageTitle>
          <AdminHeaderAction type="button" onClick={openAddPlan}>
            Add New
          </AdminHeaderAction>
        </div>

        {saveSuccess ? (
          <AdminSupportingText className="text-freshgreen mb-4">{saveSuccess}</AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div
              className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
            >
              {[
                'Plan ID',
                'Plan',
                'Current Cost',
                'Pending Cost',
                'Effective From',
                'Added',
                'Updated',
                'Updated By',
                'Actions',
              ].map((label) => (
                <div
                  key={label}
                  className={`flex items-center min-w-0${label === 'Actions' ? ' shrink-0 justify-center' : ''}`}
                >
                  <AdminTableHeader className={label === 'Actions' ? undefined : 'truncate'}>
                    {label}
                  </AdminTableHeader>
                </div>
              ))}
            </div>

            {plans.length === 0 ? (
              <div className="py-10 px-4 text-center">
                <AdminPanelTitle className="text-offblack">No pricing plans found</AdminPanelTitle>
              </div>
            ) : (
              plans.map((plan) => (
                <div
                  key={plan.id}
                  className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2"
                  style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                >
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block font-mono">{plan.id}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">{plan.planName}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block tabular-nums">
                      {formatMonthlyCostDisplay(resolveCurrentMonthlyCost(plan))}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block tabular-nums">
                      {hasPendingPriceChange(plan)
                        ? formatMonthlyCostDisplay(plan.pendingMonthlyCost)
                        : '—'}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">
                      {hasPendingPriceChange(plan)
                        ? formatEffectiveFromDisplay(plan.effectiveFrom)
                        : '—'}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">
                      {formatAuditDisplay(plan.addedOn)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">
                      {formatAuditDisplay(plan.lastUpdatedOn)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">
                      {plan.lastUpdatedBy || '—'}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center justify-center shrink-0 overflow-visible">
                    <div
                      className="relative inline-flex"
                      ref={(el) => {
                        rowActionsRefs.current[plan.id] = el;
                      }}
                    >
                      <AdminRowActionsButton
                        onClick={() => toggleRowActions(plan.id)}
                        aria-expanded={!!rowActionsOpen[plan.id]}
                        title="Actions"
                        aria-label="Row actions"
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                      {rowActionsOpen[plan.id] && rowActionsMenuLayout ? (
                        <AdminFixedRowActionsMenu
                          layout={rowActionsMenuLayout}
                          menuRef={rowActionsMenuRef}
                          placementReady={rowActionsMenuPlacementReady}
                        >
                          <AdminRowMenuItem
                            onClick={() => {
                              closeRowActions();
                              openPlanView(plan);
                            }}
                          >
                            View
                          </AdminRowMenuItem>
                          <AdminRowMenuItem
                            onClick={() => {
                              closeRowActions();
                              openPlanEdit(plan);
                            }}
                          >
                            Edit
                          </AdminRowMenuItem>
                        </AdminFixedRowActionsMenu>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DashboardMainContainer>
      </div>
    </AdminLayout>
  );
};

export default PricingPlans;
